import {
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  Validator,
} from '@angular/forms';
import * as _ from 'lodash';

import { i18nService } from '../../services/i18n.service';
import { AmountInputService } from './amount-input.service';

@Component({
  selector: 'amount-input',
  template: `
    @if (label != '') {
      <label class="input-label" [attr.for]="id">
        {{ config.label }}
        @if (!optional) {
          <sup class="asterisk">*</sup>
        }
      </label>
    }

    @if (xDeleteButton && !isDisabled) {
      <span class="x-delete" (click)="deleteField($event)">x</span>
    }
    <input
      #amountInput
      type="text"
      appParseDecimals
      decimalSeparator="{{ DECIMAL_SEPARATOR }}"
      (keypress)="_KeyPress($event)"
      (updateDecimals)="updateDecimals($event)"
      id="{{ id }}"
      class="form-control input-text"
      placeholder="{{ placeholder }}"
      [value]="data"
      [attr.maxlength]="maxlength"
      [disabled]="isDisabled"
      [class]="styleClass"
      [ngClass]="{ 'amount-error': isInvalid }"
    />

    @if (symbol) {
      <span style="margin-top:10px" class="number-span-euro">{{ symbol }}</span>
    }

    <div *ngFor="let error of errorMessages">
      @if (isInvalid && errors[error.key]) {
        <div class="error-message">
          {{ error.message }}
        </div>
      }
    </div>
  `,
  styles: [
    `
      .error-message {
        color: red;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmountInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AmountInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class AmountInputComponent
  implements ControlValueAccessor, Validator, OnInit, DoCheck
{
  @ViewChild('amountInput', { static: true }) amountInput: any;
  @Input() config: any; // Component config object

  public DECIMAL_SEPARATOR: string;
  private THOUSANDS_SEPARATOR: string;

  private format;
  private modelData: any;
  private lastValue: string = '';
  private newValue: string = '';

  public id: string = '';
  public label: string = '';
  public placeholder: string = '';
  public optional: boolean;
  public isDisabled: boolean = false;
  public styleClass: string = '';
  public inputFormat: string = '';
  public xDeleteButton: boolean = true;
  public errorMessages: Object[] = [];
  public errors: Object = {};
  public isInvalid: boolean = false;
  public data: any;
  public symbol: string;
  public maxlength: number = 1;

  constructor(
    private el: ElementRef,
    public _i18n: i18nService,
    private amountInputService: AmountInputService,
  ) {}

  private getFormat(inputFormat: string) {
    let regex: string = '^$|^[0-9]*(' + this.DECIMAL_SEPARATOR + '[0-9]{0,2})?$';
    let [integer, fraction = ''] = (inputFormat || '').toString().split('-');

    if (integer === '' || integer === '*') {
      regex = '^$|^[0-9]*';
    } else {
      regex = '^$|^[0-9]{0,' + integer + '}';
    }

    if (fraction === '' || fraction === '*') {
      regex += '(' + this.DECIMAL_SEPARATOR + '[0-9]*)?$';
    } else {
      regex += '(' + this.DECIMAL_SEPARATOR + '[0-9]{0,' + fraction + '})?$';
      this.amountInputService.setFractionSize(parseInt(fraction, 10));
    }

    return regex;
  }

  private getFormatRegExp(inputFormat: string) {
    return new RegExp(inputFormat);
  }

  updateDecimals(event) {
    let currentValue = event;

    if (currentValue !== '') {
      if (!this.format.test(currentValue)) {
        this.amountInput.nativeElement.value = this.lastValue;
        this.newValue = this.lastValue;
        this.data = this.lastValue;

        // update the form
        this.modelData = parseFloat(
          (<string>this.data).replace(this.DECIMAL_SEPARATOR, '.'),
        );
        this.propagateChange(this.modelData);
      } else {
        this.lastValue = currentValue;
        this.data = currentValue;
        this.modelData = parseFloat(
          (<string>this.data).replace(this.DECIMAL_SEPARATOR, '.'),
        );
        this.propagateChange(this.modelData);
      }
    } else {
      this.modelData = currentValue;
      this.propagateChange(currentValue);
    }
  }

  public deleteField(event) {
    this.data = '';
    this.propagateChange(this.data);
  }

  // this is the initial value set to the component
  public writeValue(obj: any) {
    this.data = obj.replace('.', ',');
  }

  // registers 'fn' that will be fired wheb changes are made
  // this is how we emit the changes back to the form
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  // validates the form, returns null when valid else the validation object
  // in this case we're checking if the json parsing has passed or failed from the onChange method
  public validate(c: UntypedFormControl) {
    setTimeout(() => {
      this.isInvalid = c.invalid && c.dirty;
      this.errors = c.errors;
    }, 0);

    return null;
  }

  // not used, used for touch input
  public registerOnTouched() {}

  _KeyPress(event: any) {
    if (!this.format.test(event.target.value)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }

  // change events from the text input
  public onChange(event: any) {}

  // the method set in registerOnChange to emit changes back to the form
  private propagateChange = (_: any) => {};

  private getComponentConfig(config: any) {
    this.id = _.isUndefined(config.id) ? '' : config.id;
    this.label = _.isUndefined(config.label) ? '' : config.label;
    this.placeholder = _.isUndefined(config.placeholder)
      ? '0' + this.DECIMAL_SEPARATOR + '00'
      : config.placeholder;
    this.optional = _.isUndefined(config.optional) ? false : config.optional;
    this.inputFormat = _.isUndefined(config.inputFormat)
      ? '^$|^[0-9]*(' + this.DECIMAL_SEPARATOR + '[0-9]{0,2})?$'
      : this.getFormat(config.inputFormat);
    this.isDisabled = _.isUndefined(config.isDisabled) ? false : config.isDisabled;
    this.styleClass = _.isUndefined(config.styleClass) ? '' : config.styleClass;
    this.xDeleteButton = _.isUndefined(config.xDeleteButton)
      ? true
      : config.xDeleteButton;
    this.symbol = _.isUndefined(config.symbol) ? '' : config.symbol;
    this.errorMessages = _.isUndefined(config.errorMessages) ? [] : config.errorMessages;
    this.maxlength = _.isUndefined(config.maxlength) ? 10 : config.maxlength;

    this.format = this.getFormatRegExp(this.inputFormat);
  }

  ngDoCheck() {
    const config: any = this.config;
    this.getComponentConfig(config);
  }

  ngOnInit() {
    const config: any = this.config;

    this.amountInputService.getLocaleSeparators();
    this.DECIMAL_SEPARATOR = this.amountInputService.getDecimalSeparator();
    this.THOUSANDS_SEPARATOR = this.amountInputService.getThousandsSeparator();

    if (_.isUndefined(config.id) || config.id === '') {
      throw new Error('VASS core: id is required');
    } else {
      this.getComponentConfig(config);
    }
  }
}
